import { resolveFlowControlExpandIdFromCueRow } from '@/helpers/flow-control-playing-segment.helper';
import {
  findSimulationRowIndex,
  resolveSimulationSegmentPlayPointer,
  type SimulationCuePlaylistContext,
  type SimulationSegmentAnchor,
} from '@/helpers/simulation-cue-engine.helper';
import { resolveNextCuePreviewRow, type LiveNextCueTarget } from '@/helpers/resolve-next-cue-preview-row.helper';
import type { SimulationCueSheetRow } from '@/types/player/simulation-cue-sheet.types';

export type ExpectedCueWindowAfterJump = {
  targetItemId?: string;
  targetContext?: SimulationCuePlaylistContext;
  targetExpandId?: string;
  expectedNext?: LiveNextCueTarget;
  expectedNextExpandId?: string;
};

function contextFromRow(row: SimulationCueSheetRow): SimulationCuePlaylistContext {
  return {
    scope: row.scope,
    slotId: row.slotId,
    cueItemId: row.cueItemId,
  };
}

function isSameCueIdentity(
  left: Pick<SimulationCueSheetRow, 'itemId' | 'scope' | 'slotId' | 'cueItemId'>,
  right: Pick<SimulationCueSheetRow, 'itemId' | 'scope' | 'slotId' | 'cueItemId'>,
): boolean {
  return (
    left.itemId === right.itemId &&
    (left.cueItemId || '') === (right.cueItemId || '') &&
    (left.scope || '') === (right.scope || '') &&
    (left.slotId || '') === (right.slotId || '')
  );
}

/** Current + Next the rundown should paint after a jump/segment take. */
export function resolveExpectedCueWindowAfterJump(params: {
  rows: SimulationCueSheetRow[];
  targetItemId?: string;
  playlistContext?: SimulationCuePlaylistContext;
  playSegment?: { anchor: SimulationSegmentAnchor; slotId?: string };
}): ExpectedCueWindowAfterJump {
  const fallback: ExpectedCueWindowAfterJump = {
    targetItemId: params.targetItemId,
    targetContext: params.playlistContext,
  };
  if (params.rows.length === 0) {
    return fallback;
  }

  let targetIndex = -1;
  if (params.targetItemId) {
    targetIndex = findSimulationRowIndex(params.rows, params.targetItemId, params.playlistContext);
  } else if (params.playSegment) {
    targetIndex = resolveSimulationSegmentPlayPointer(
      params.rows,
      params.playSegment.anchor,
      params.playSegment.slotId,
    );
  }
  const targetRow = targetIndex >= 0 ? params.rows[targetIndex] : undefined;
  if (!targetRow) {
    return fallback;
  }

  const nextRow = resolveNextCuePreviewRow(params.rows, targetIndex);
  const expectedNext =
    nextRow?.itemId && !isSameCueIdentity(targetRow, nextRow)
      ? {
          itemId: nextRow.itemId,
          cueItemId: nextRow.cueItemId,
          scope: nextRow.scope,
          slotId: nextRow.slotId,
        }
      : undefined;

  return {
    targetItemId: targetRow.itemId,
    targetContext: contextFromRow(targetRow),
    targetExpandId: resolveFlowControlExpandIdFromCueRow(targetRow) ?? undefined,
    expectedNext,
    expectedNextExpandId: resolveFlowControlExpandIdFromCueRow(nextRow) ?? undefined,
  };
}
